import { MontoCRC, MontoUSD } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useGenerarArchivoBNSemana,
  useGenerarArchivoSemana,
  useVistaPreviaArchivoSemana,
} from '@/hooks/useArchivoBancoSemana';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import { COLOR_BANCO } from '@/lib/coloresBanco';
import { descargarArchivoTexto } from '@/lib/descargar-archivo';
import { cn } from '@/lib/utils';
import { bancoDeCuentaBC } from '@adelante/shared';
import type {
  ArchivoBancoBNResultado,
  ArchivoBancoSemanaGuardado,
  ArchivoBancoSemanaResultado,
  BancoPago,
  MotivoExclusionSemanaBanco,
  PagoSemanaBanco,
} from '@adelante/shared';
import { DownloadSimple, Warning } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Props {
  semanaId: number | null;
  open: boolean;
  onClose: () => void;
}

/** Fecha local de hoy (AAAA-MM-DD) — `toISOString` usa UTC y en Costa Rica puede adelantar el día. */
function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const MOTIVO: Record<MotivoExclusionSemanaBanco, (moneda: string) => string> = {
  'sin-proveedor': () => 'El concepto no tiene proveedor — asignalo en Configuración → Conceptos',
  'sin-cuenta': (moneda) => `El proveedor no tiene cuenta en ${moneda} — cargala en Proveedores`,
  'iban-formato': () =>
    'IBAN con formato inválido (debe ser CR + 20 dígitos) — corregilo en Proveedores',
  'sin-cedula': () => 'El proveedor no tiene una cédula válida — cargala en Proveedores',
  'cedula-incompleta': () =>
    'Cédula de 9 dígitos con formato de jurídica (3-101…): parece faltar un dígito',
};

const NOTA_BANCO: Record<BancoPago, string> = {
  BAC: 'BAC no cobra comisión, pero el archivo queda aplicado en la noche o al día siguiente.',
  BN: 'El BN aplica inmediato. Los pagos a cuentas del BN van por .TRF (sin comisión) y los de otros bancos por .SIN (SINPE, con comisión). Abrilos en el Módulo Local del BN → Formato IBAN → Revisar archivo.',
};

const ESTILO_TAB: Record<BancoPago, string> = {
  BAC: COLOR_BANCO.BAC.activo,
  BN: COLOR_BANCO.BN.activo,
};

function etiquetaDestino(p: PagoSemanaBanco): string {
  return `…${p.iban.slice(-4)} · ${p.bancoCuenta === 'OTRO' ? 'otro banco' : p.bancoCuenta}`;
}

/** Cómo viaja el pago: el aviso de comisión solo existe para el BN → otro banco (SINPE). */
function TipoPago({ tipo }: { tipo: PagoSemanaBanco['tipo'] }) {
  if (tipo === 'SINPE')
    return <span className="font-medium text-amber-700">SINPE · con comisión</span>;
  if (tipo === 'TRF') return <span className="text-muted-foreground">TRF · sin comisión</span>;
  return <span className="text-muted-foreground">BAC</span>;
}

export function GenerarArchivoBancoSemanaDialog({ semanaId, open, onClose }: Props) {
  const { data: cuentasBancoData } = useCuentasBancoBC();
  const cuentasBanco = cuentasBancoData?.data ?? [];

  const [banco, setBanco] = useState<BancoPago>('BAC');
  const [cuenta, setCuenta] = useState('');
  const [planCode, setPlanCode] = useState('');
  const [envioNo, setEnvioNo] = useState('');
  const [transferenciaNo, setTransferenciaNo] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [resultadoBAC, setResultadoBAC] = useState<ArchivoBancoSemanaResultado | null>(null);
  const [resultadoBN, setResultadoBN] = useState<ArchivoBancoBNResultado | null>(null);
  const generarBAC = useGenerarArchivoSemana();
  const generarBN = useGenerarArchivoBNSemana();

  // Solo las cuentas de origen del banco elegido: un archivo es de un banco y una moneda.
  const cuentasDelBanco = useMemo(
    () => cuentasBanco.filter((c) => bancoDeCuentaBC(c.displayName) === banco),
    [cuentasBanco, banco],
  );

  const hayResultado = banco === 'BAC' ? !!resultadoBAC : !!resultadoBN;
  const { data: vistaData, isFetching } = useVistaPreviaArchivoSemana(
    semanaId,
    cuenta,
    open && !hayResultado,
  );
  const vista = vistaData?.data ?? null;
  const guardado: ArchivoBancoSemanaGuardado | null = vista?.guardado ?? null;

  // Cuenta inicial del banco: la primera en colones (la de uso más común).
  useEffect(() => {
    if (!open || cuentasDelBanco.length === 0) return;
    if (!cuentasDelBanco.some((c) => c.number === cuenta)) {
      setCuenta(
        (
          cuentasDelBanco.find((c) => !c.currencyCode || c.currencyCode === 'CRC') ??
          cuentasDelBanco[0]
        )?.number ?? '',
      );
    }
  }, [open, cuenta, cuentasDelBanco]);

  // Al cambiar de cuenta (o llegar el archivo BAC ya generado de esa cuenta): se limpia el formulario
  // o se llena con lo del archivo guardado.
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reaccionar al cambio de cuenta o a que llegue el archivo guardado
  useEffect(() => {
    setResultadoBAC(null);
    setResultadoBN(null);
    if (guardado) {
      setPlanCode(guardado.planCode);
      setEnvioNo(String(guardado.envioNo));
      setFecha(guardado.fecha);
      return;
    }
    setPlanCode('');
    setEnvioNo('');
    setFecha(hoyISO());
  }, [cuenta, guardado?.generadoEn]);

  // Cada cuenta BAC tiene su propio plan (BOK): se sugiere el último que se usó con esa cuenta
  // (en propuestas o en Pagos Semanales), solo si todavía no se escribió nada.
  const ultimoPlan = vista?.ultimo?.planCode;
  useEffect(() => {
    if (open && banco === 'BAC' && !guardado && !planCode && ultimoPlan) setPlanCode(ultimoPlan);
  }, [open, banco, guardado, planCode, ultimoPlan]);

  const envioNumero = Number(envioNo);
  const envioValido = Number.isInteger(envioNumero) && envioNumero > 0 && envioNumero <= 999;
  const transferenciaNumero = Number(transferenciaNo);
  const transferenciaValida =
    Number.isInteger(transferenciaNumero) &&
    transferenciaNumero >= 1 &&
    transferenciaNumero <= 99999;
  const Monto = vista?.moneda === 'D' ? MontoUSD : MontoCRC;
  const nombreMoneda = vista?.moneda === 'D' ? 'dólares' : 'colones';
  const hayPagos = !!vista && vista.bloqueos.length === 0 && vista.pagos.length > 0;
  const generando = generarBAC.isPending || generarBN.isPending;
  const puedeGenerar =
    hayPagos &&
    !generando &&
    (banco === 'BAC' ? !!planCode.trim() && envioValido : transferenciaValida);

  function cambiarBanco(nuevo: BancoPago) {
    if (nuevo === banco) return;
    setBanco(nuevo);
    setCuenta('');
    setResultadoBAC(null);
    setResultadoBN(null);
  }

  function cerrar() {
    setResultadoBAC(null);
    setResultadoBN(null);
    onClose();
  }

  function generarArchivo() {
    if (!semanaId || !puedeGenerar) return;
    const alFallar = (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Error al generar el archivo');
    if (banco === 'BAC') {
      generarBAC.mutate(
        {
          semanaId,
          cuentaBancoOrigen: cuenta,
          planCode: planCode.trim(),
          envioNo: envioNumero,
          fecha,
        },
        {
          onSuccess: (r) =>
            r.data
              ? setResultadoBAC(r.data)
              : toast.error(r.error?.mensaje ?? 'Error al generar el archivo'),
          onError: alFallar,
        },
      );
    } else {
      generarBN.mutate(
        { semanaId, cuentaBancoOrigen: cuenta, transferenciaNo: transferenciaNumero, fecha },
        {
          onSuccess: (r) =>
            r.data
              ? setResultadoBN(r.data)
              : toast.error(r.error?.mensaje ?? 'Error al generar el archivo'),
          onError: alFallar,
        },
      );
    }
  }

  // Lo que se puede descargar del BAC: el archivo recién generado, o el ya guardado de esta cuenta.
  const descargableBAC = resultadoBAC ?? guardado;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Archivos de banco — Pagos Semanales</DialogTitle>
          <DialogDescription>
            Arma el archivo de cada banco con los pagos de la semana asignados a él (columna{' '}
            <strong>Banco</strong>). Proveedores Crédito no entra acá: sale del archivo de Pago
            Proveedores.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <div
              role="tablist"
              aria-label="Banco"
              className="inline-flex overflow-hidden rounded-md border border-border text-sm font-medium"
            >
              {(['BAC', 'BN'] as const).map((b) => (
                <button
                  key={b}
                  type="button"
                  role="tab"
                  aria-selected={banco === b}
                  onClick={() => cambiarBanco(b)}
                  className={cn(
                    'px-4 py-1.5 transition-colors',
                    banco === b
                      ? ESTILO_TAB[b]
                      : 'bg-transparent text-muted-foreground hover:bg-zinc-100',
                  )}
                >
                  {b === 'BAC' ? 'BAC' : 'Banco Nacional'}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{NOTA_BANCO[banco]}</p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="sem-cuenta">Cuenta bancaria de origen</Label>
            <select
              id="sem-cuenta"
              value={cuenta}
              onChange={(e) => setCuenta(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {cuentasDelBanco.length === 0 && <option value="">Cargando cuentas…</option>}
              {cuentasDelBanco.map((c) => (
                <option key={c.number} value={c.number}>
                  {c.displayName} ({c.number}) — {c.currencyCode === 'USD' ? 'dólares' : 'colones'}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Un archivo por cuenta: elegí una cuenta en dólares para los pagos en dólares.
            </p>
          </div>

          {banco === 'BAC' ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="sem-plan">Código Plan</Label>
                  <Input
                    id="sem-plan"
                    value={planCode}
                    onChange={(e) => setPlanCode(e.target.value)}
                    placeholder="Ej. BOK3"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sem-envio">Número de envío</Label>
                  <Input
                    id="sem-envio"
                    type="number"
                    min={1}
                    max={999}
                    value={envioNo}
                    onChange={(e) => setEnvioNo(e.target.value)}
                    placeholder="Ej. 321"
                    autoComplete="off"
                  />
                  {vista?.ultimo && (
                    <p className="text-xs text-muted-foreground">
                      Último de esta cuenta: {vista.ultimo.envioNo} ({vista.ultimo.planCode})
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sem-fecha">Fecha</Label>
                  <Input
                    id="sem-fecha"
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                  />
                </div>
              </div>
              {envioValido && (
                <p className="text-xs text-muted-foreground">
                  Consecutivos: {envioNumero * 100 + 1}, {envioNumero * 100 + 2}, … (envío + 01, 02,
                  …)
                </p>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="sem-transferencia">Número de transferencia</Label>
                <Input
                  id="sem-transferencia"
                  type="number"
                  min={1}
                  max={99999}
                  value={transferenciaNo}
                  onChange={(e) => setTransferenciaNo(e.target.value)}
                  placeholder="Ej. 1250"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sem-fecha-bn">Fecha de aplicación</Label>
                <Input
                  id="sem-fecha-bn"
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </div>
            </div>
          )}

          {isFetching && !vista && <p className="text-sm text-muted-foreground">Revisando…</p>}

          {vista && vista.bloqueos.length > 0 && (
            <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <p className="flex items-center gap-2 font-medium text-destructive">
                <Warning className="h-4 w-4" weight="bold" aria-hidden="true" />
                No se puede generar todavía
              </p>
              <ul className="list-inside list-disc text-xs">
                {vista.bloqueos.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          )}

          {vista && vista.pagos.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between text-sm">
                <span>
                  <strong>{vista.pagos.length}</strong> pago(s) en {nombreMoneda} · semana{' '}
                  {vista.semanaNumero}/{vista.semanaAnio}
                </span>
                <span className="font-mono font-semibold">
                  <Monto monto={vista.total} />
                </span>
              </div>
              {Math.abs(vista.totalMarcado - vista.total) > 0.005 && (
                <p className="text-xs text-muted-foreground">
                  Asignados a {banco} sin pagar: <Monto monto={vista.totalMarcado} /> — la
                  diferencia son los excluidos de abajo.
                </p>
              )}
              <div className="max-h-56 overflow-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/60 text-left">
                    <tr>
                      <th className="px-2 py-1 font-medium">Proveedor</th>
                      <th className="px-2 py-1 font-medium">Cuenta destino</th>
                      <th className="px-2 py-1 font-medium">Conceptos</th>
                      <th className="px-2 py-1 text-right font-medium">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vista.pagos.map((p) => (
                      <tr
                        key={`${p.proveedorId}-${p.cuentaId}`}
                        className="border-t border-border/50 align-top"
                      >
                        <td className="px-2 py-1">
                          {p.nombre}
                          {p.vendorCode && (
                            <span className="ml-1 font-mono text-muted-foreground">
                              {p.vendorCode}
                            </span>
                          )}
                          {p.advertencia && (
                            <span className="block text-amber-700">{p.advertencia}</span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <span title={p.iban}>{etiquetaDestino(p)}</span>
                          <span className="block">
                            <TipoPago tipo={p.tipo} />
                          </span>
                        </td>
                        <td className="px-2 py-1 text-muted-foreground">
                          {p.conceptos.map((c) => c.concepto).join(', ')}
                        </td>
                        <td className="px-2 py-1 text-right font-mono">
                          <Monto monto={p.total} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {vista && vista.excluidos.length > 0 && (
            <div className="space-y-1 rounded-md border border-amber-400/60 bg-amber-50 p-3 text-xs">
              <p className="font-medium">
                {vista.excluidos.length} pago(s) asignados a {banco} quedan fuera del archivo:
              </p>
              <ul className="space-y-0.5">
                {vista.excluidos.map((e) => (
                  <li key={`${e.conceptoId}-${e.motivo}`}>
                    <strong>{e.concepto}</strong>
                    {e.proveedor ? ` (${e.proveedor})` : ''} · <Monto monto={e.monto} /> —{' '}
                    {MOTIVO[e.motivo]?.(nombreMoneda)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {vista && vista.otroBanco.cantidad > 0 && (
            <p className="text-xs text-muted-foreground">
              Hay {vista.otroBanco.cantidad} pago(s) en {nombreMoneda} (
              <Monto monto={vista.otroBanco.total} />) asignados a{' '}
              {vista.otroBanco.banco === 'BN' ? 'el Banco Nacional' : 'BAC'}: van en el archivo de
              ese banco.
            </p>
          )}
          {vista && vista.otraMoneda.cantidad > 0 && (
            <p className="text-xs text-muted-foreground">
              Hay {vista.otraMoneda.cantidad} pago(s) asignados a {banco} en{' '}
              {vista.otraMoneda.moneda === 'D' ? 'dólares' : 'colones'} que van en el archivo de una
              cuenta en {vista.otraMoneda.moneda === 'D' ? 'dólares' : 'colones'}.
            </p>
          )}

          {banco === 'BAC' && descargableBAC && (
            <div className="space-y-2 rounded-md border border-adelante-primary/30 bg-adelante-primary/5 p-3 text-sm">
              {!resultadoBAC && guardado && (
                <p className="text-xs text-muted-foreground">
                  Ya generado el{' '}
                  {new Date(guardado.generadoEn).toLocaleString('es-CR', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}{' '}
                  — descargalo de nuevo, o generá uno nuevo si cambió algo.
                </p>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Monto</p>
                  <p className="font-mono text-base font-semibold">
                    <Monto monto={descargableBAC.montoTotal} />
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Cantidad de líneas</p>
                  <p className="font-mono text-base font-semibold">
                    {descargableBAC.totalIncluidas}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Consecutivo</p>
                  <p className="font-mono text-base font-semibold">
                    {descargableBAC.consecutivoInicial === descargableBAC.consecutivoFinal
                      ? descargableBAC.consecutivoInicial
                      : `${descargableBAC.consecutivoInicial}–${descargableBAC.consecutivoFinal}`}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Monto y cantidad de líneas son los que BAC pide al subir el archivo — para cotejar.
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    descargarArchivoTexto(
                      descargableBAC.contenidoCsv,
                      descargableBAC.nombreArchivoSugerido,
                      false,
                    )
                  }
                >
                  <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                  Descargar CSV
                </Button>
                <Button
                  onClick={() =>
                    descargarArchivoTexto(
                      descargableBAC.contenidoPrn,
                      descargableBAC.nombreArchivoPrnSugerido,
                      true,
                    )
                  }
                >
                  <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                  Descargar .prn
                </Button>
              </div>
            </div>
          )}

          {banco === 'BN' && resultadoBN && (
            <div className="space-y-2 rounded-md border border-adelante-primary/30 bg-adelante-primary/5 p-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Monto total</p>
                  <p className="font-mono text-base font-semibold">
                    <Monto monto={resultadoBN.montoTotal} />
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Créditos</p>
                  <p className="font-mono text-base font-semibold">{resultadoBN.totalIncluidas}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                La línea TESTKEY no va en el archivo: el Módulo Local la devuelve al revisarlo y se
                digita al confirmar en Internet Banking.
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                {resultadoBN.trf && (
                  <Button
                    onClick={() =>
                      descargarArchivoTexto(
                        resultadoBN.trf?.contenido ?? '',
                        resultadoBN.trf?.nombreArchivoSugerido ?? 'PAGOS_BN.TRF',
                        true,
                      )
                    }
                  >
                    <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                    Descargar .TRF ({resultadoBN.trf.totalCreditos})
                  </Button>
                )}
                {resultadoBN.sin && (
                  <Button
                    onClick={() =>
                      descargarArchivoTexto(
                        resultadoBN.sin?.contenido ?? '',
                        resultadoBN.sin?.nombreArchivoSugerido ?? 'PAGOS_OTROS_BANCOS.SIN',
                        true,
                      )
                    }
                  >
                    <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                    Descargar .SIN ({resultadoBN.sin.totalCreditos})
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="flex min-w-0 flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={cerrar}>
              Cerrar
            </Button>
            {!(banco === 'BAC' ? resultadoBAC : resultadoBN) && (
              <Button onClick={generarArchivo} disabled={!puedeGenerar}>
                {generando
                  ? 'Generando...'
                  : banco === 'BAC' && guardado
                    ? 'Generar de nuevo'
                    : 'Generar archivo'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
